// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import PropTypes from "prop-types";

import { classes } from "utils/classes";

import { PageBanner } from "./PageBanner";
import styles from "./PageContent.scss";

/**
 * @typedef {Object} PageContentProps
 * @property {React.ReactNode} [children] - The content to be rendered inside the page
 * @property {string} [className] - Additional CSS class names for the container
 * @property {string} [bannerClassName] - Additional CSS class names for the banner
 * @property {string} [bannerText] - Text to display in the page banner
 * @property {string} [bannerIcon] - GrabsonIcon name for the banner icon
 * @property {string|React.ReactNode} [bannerTooltipContent] - Content for the banner tooltip
 * @property {string} [dataTestId="page-content-container"] - Test ID for testing purposes
 */

/**
 * Generic page layout that works well with sidebar.
 * Most of pages in our system use this component alog with [PageHeader](#pageheader).
 * @param {PageContentProps} props - The component props
 * @returns {JSX.Element} The rendered page content component
 */
export const PageContent = ({
  children,
  className,
  bannerClassName,
  bannerText,
  bannerIcon,
  bannerTooltipContent,
  dataTestId = "page-content-container",
}) => (
  <div
    className={classes(
      bannerText ? styles.containerWithBanner : styles.container,
      className
    )}
    data-test-id={dataTestId}
  >
    {bannerText && (
      <PageBanner
        className={classes(styles.banner, bannerClassName)}
        text={bannerText}
        icon={bannerIcon}
        tooltipContent={bannerTooltipContent}
      />
    )}
    {children}
  </div>
);

PageContent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  bannerClassName: PropTypes.string,
  bannerText: PropTypes.string,
  /* GrabsonIcon name */
  bannerIcon: PropTypes.string,
  bannerTooltipContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  dataTestId: PropTypes.string,
};
